import React, { useEffect, useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import TeamShowcase from "../../pages/TeamShowcase";
import { FaWhatsapp, FaFacebook, FaLinkedin, FaInstagram, FaTiktok } from "react-icons/fa";

// Icons
const TeamIcon = () => <span className="text-3xl">👥</span>;
const GameIcon = () => <span className="text-3xl">🎮</span>;
const YearIcon = () => <span className="text-3xl">🏆</span>;
const AndroidIcon = () => <span className="text-3xl">📱</span>;
const IosIcon = () => <span className="text-3xl">🍎</span>;

const About = () => {
  const [employeeCount, setEmployeeCount] = useState(0);
  const [gamesCount, setGamesCount] = useState(0);
  const [yearsCount, setYearsCount] = useState(0);
  const [androidApps, setAndroidApps] = useState(0);
  const [iosApps, setIosApps] = useState(0);

  const statsRef = useRef(null);
  const isStatsInView = useInView(statsRef, { once: true, amount: 0.3 });

  const animateNumber = (endValue, setState, speed = 30) => {
    let count = 0;
    const interval = setInterval(() => {
      count += 1;
      setState(count);
      if (count >= endValue) {
        clearInterval(interval);
      }
    }, speed);
  };

  useEffect(() => {
    if (isStatsInView) {
      animateNumber(60, setEmployeeCount, 30);
      animateNumber(800, setGamesCount, 5);
      animateNumber(8, setYearsCount, 100);
      animateNumber(500, setAndroidApps, 5);
      animateNumber(100, setIosApps, 15);
    }
  }, [isStatsInView]);

  const gameIndustryImageUrl = "https://images.unsplash.com/photo-1552820728-8b83bb6b773f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80";
  const fallbackImageUrl = "https://images.unsplash.com/photo-1552820728-8b83bb6b773f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80";

  return (
    <section className="w-full py-20 px-4 sm:px-6 lg:px-8 font-roboto bg-gradient-to-br from-white via-gray-50 to-white overflow-hidden relative">
      {/* Animated background orbs with red accent */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-200/40 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-rose-200/40 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-red-100/30 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Heading without underline */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold">
            <span className="bg-gradient-to-r from-red-600 via-red-500 to-rose-500 bg-clip-text text-transparent">
              About Us
            </span>
          </h2>
          <p className="mt-4 text-gray-600 max-w-2xl mx-auto text-lg">
            Crafting immersive gaming experiences with passion and precision.
          </p>
        </motion.div>

        {/* Main content: text + image */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left column – text */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h3 className="text-2xl font-semibold mb-4 bg-gradient-to-r from-red-600 to-rose-500 bg-clip-text text-transparent">
              Who We Are
            </h3>
            <p className="text-gray-700 leading-relaxed">
              At <span className="font-bold bg-gradient-to-r from-red-600 to-rose-500 bg-clip-text text-transparent">UB Creations Pvt Ltd</span>, we're a close‑knit team of developers, designers, and artists united by a passion for gaming. Operating entirely in‑house gives us full creative control and the agility to turn bold ideas into polished, engaging experiences.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              From concept art to final release, every project is crafted with meticulous attention to detail and a deep understanding of what makes players tick. We don't just build games we build worlds.
            </p>

            {/* Platform expertise row – glassmorphic with red accent */}
            <motion.div
              className="flex items-center gap-4 mt-6 bg-white/60 backdrop-blur-sm p-4 rounded-xl border border-gray-200 shadow-sm hover:border-red-200 transition-colors"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <div className="flex items-center gap-2">
                <AndroidIcon />
                <span className="text-sm font-medium text-gray-700">Android</span>
              </div>
              <div className="flex items-center gap-2">
                <IosIcon />
                <span className="text-sm font-medium text-gray-700">iOS</span>
              </div>
              <span className="text-xs text-gray-500 ml-2">We develop for both platforms</span>
            </motion.div>

            {/* Mission snippet – glassmorphic with red gradient border */}
            <div className="mt-8 p-5 bg-white/60 backdrop-blur-sm rounded-xl border border-red-100 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-start gap-3">
                <span className="text-3xl">🎯</span>
                <div>
                  <h4 className="font-bold bg-gradient-to-r from-red-600 to-rose-500 bg-clip-text text-transparent">Our Mission</h4>
                  <p className="text-gray-600 text-sm">
                    To push the boundaries of interactive entertainment by creating games that inspire, challenge, and connect players worldwide.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right column – stylish circular image with red glow */}
          <motion.div
            className="relative flex justify-center"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            <div className="relative w-80 h-80 md:w-96 md:h-96 group">
              {/* Outer glow effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-rose-500 rounded-full blur-2xl opacity-50 group-hover:opacity-80 transition duration-500" />
              {/* Circular image container */}
              <div className="relative bg-white/40 backdrop-blur-sm rounded-full p-2 shadow-xl">
                <div className="rounded-full overflow-hidden bg-white/20 w-full h-full">
                  <img
                    src={gameIndustryImageUrl}
                    alt="Game development studio"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = fallbackImageUrl;
                    }}
                  />
                </div>
              </div>
              {/* Optional decorative ring */}
              <div className="absolute inset-0 rounded-full border-2 border-red-300/50 pointer-events-none"></div>
            </div>
            {/* Decorative floating element */}
            <div className="absolute -bottom-6 -right-6 w-20 h-20 bg-gradient-to-br from-red-400 to-rose-400 rounded-full blur-2xl opacity-30 -z-10"></div>
          </motion.div>
        </div>

        {/* Social Media Icons Row - Only these 5 platforms */}
        <motion.div
          className="flex flex-wrap items-center justify-center gap-4 mt-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          {/* Facebook */}
          <a
            href="https://www.facebook.com/share/1CVBfEbtnv/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-white/80 backdrop-blur-sm px-5 py-2.5 rounded-full shadow-sm hover:shadow-md transition border border-gray-200 text-gray-700 hover:text-blue-600 group"
          >
            <FaFacebook className="text-xl" />
            <span className="text-sm font-medium">Facebook</span>
          </a>

          {/* LinkedIn */}
          <a
            href="https://www.linkedin.com/company/ubsoftgames"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-white/80 backdrop-blur-sm px-5 py-2.5 rounded-full shadow-sm hover:shadow-md transition border border-gray-200 text-gray-700 hover:text-blue-700 group"
          >
            <FaLinkedin className="text-xl" />
            <span className="text-sm font-medium">LinkedIn</span>
          </a>

          {/* WhatsApp */}
          <a
            href="https://wa.me/+92 300 1381308"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-white/80 backdrop-blur-sm px-5 py-2.5 rounded-full shadow-sm hover:shadow-md transition border border-gray-200 text-gray-700 hover:text-green-500 group"
          >
            <FaWhatsapp className="text-xl" />
            <span className="text-sm font-medium">WhatsApp</span>
          </a>

          {/* Instagram */}
          <a
            href="https://www.instagram.com/creations.ub?igsh=MTQ1YXprdGtvNGhnYQ=="
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-white/80 backdrop-blur-sm px-5 py-2.5 rounded-full shadow-sm hover:shadow-md transition border border-gray-200 text-gray-700 hover:text-pink-600 group"
          >
            <FaInstagram className="text-xl" />
            <span className="text-sm font-medium">Instagram</span>
          </a>

          {/* TikTok */}
          <a
            href="https://www.tiktok.com/@ubcreationspvt?_r=1&_t=ZN-95aEJjcvVag"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-white/80 backdrop-blur-sm px-5 py-2.5 rounded-full shadow-sm hover:shadow-md transition border border-gray-200 text-gray-700 hover:text-black group"
          >
            <FaTiktok className="text-xl" />
            <span className="text-sm font-medium">TikTok</span>
          </a>
        </motion.div>

        {/* Additional platform note */}
        <motion.p
          className="text-center text-xs text-gray-400 mt-6 mb-5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          * We develop for both Android and iOS using cutting edge cross platform tools and native expertise.
        </motion.p>
      </div>
      <TeamShowcase />
    </section>
  );
};

export default About;